/* eslint-disable camelcase */
import { Vue, Component } from 'vue-property-decorator'
import { FieldFlagsBag, Validator } from 'vee-validate'
import { isEmpty } from 'lodash'
import sessionStore from '@/session/sessionStore'
import moment from 'moment'
import Quill from 'quill'
import { ColDef, ColGroupDef, Column, GridApi, RowNode } from '@ag-grid-enterprise/all-modules'
import { CancelToken } from 'axios'
import { getPatientTimeOld } from '@/utils/fieldTransformations'
import { GridUtils } from '@/shared/util/grid-utils'
import { HelperUtils } from '@/shared/util/helper-utils'
import { StrictNullable } from '@/shared/types/app.types'

@Component
export class HelpersMixin extends Vue {
    get $_anapathPrinterInstallerUrl(): string {
        return HelperUtils.anapathPrinterInstallerUrl
    }

    $_setSummaryInterval(handler: Function): StrictNullable<number> {
        return HelperUtils.setSummaryInterval(handler)
    }

    $_removeExternalOptionFromEntityObjects(entity: any): any {
        return HelperUtils.removeExternalOptionFromEntityObjects(entity)
    }

    $_hasLabOperation(laboratoryId: number, operationCode: string): boolean {
        return HelperUtils.hasLabOperation(laboratoryId, operationCode)
    }
    $_hasOperationInCurrentLaboratory(operationCode: string): boolean {
        return HelperUtils.hasOperationInCurrentLaboratory(operationCode)
    }
    $_hasOperationInAnyLaboratory(operationCode: string): boolean {
        return HelperUtils.hasOperationInAnyLaboratory(operationCode)
    }
    $_hasOperationStartingWith(operationPrefix: string): boolean {
        return HelperUtils.hasOperationStartingWith(operationPrefix)
    }
    $_hasLocalOperationStartingWith(operationPrefix: string): boolean {
        return HelperUtils.hasLocalOperationStartingWith(operationPrefix)
    }

    $_getPatientTimeOld(patBirthdate: any): string {
        return getPatientTimeOld(patBirthdate)
    }

    $_updateSideBarOptionChildren(options: any[], id: number, newOptions: any[]): void {
        HelperUtils.updateSideBarOptionChildren(options, id, newOptions)
    }

    async $_processBasicConcurrencyConflict(entityName: any, error: any, updateEntityAndTryAgainFunction: Function): Promise<any> {
        const basicConcurrencyQuestion = this.$tc('global.error.basicConcurrency', 1, {
            username: error.response.data.currentConcurrencyUpdatedBy.username,
            timeSince: moment(error.response.data.currentConcurrencyUpdatedAt).fromNow(),
            entityName
        })
        if (await this.$_showConfirmationModal(basicConcurrencyQuestion)) {
            return updateEntityAndTryAgainFunction()
        }
        return null
    }

    $_filterPatientColumns(columns: (ColDef | ColGroupDef)[], middleNameId: string, surnameTwoId: string): (ColDef | ColGroupDef)[] {
        return GridUtils.filterPatientColumns(columns, middleNameId, surnameTwoId)
    }

    $_processCellOutput(column: Column, value: any, node?: RowNode): string {
        return GridUtils.processCellOutput(column, value, node)
    }

    $_removeExtensionFromFilename(filename: string): string {
        return HelperUtils.removeExtensionFromFilename(filename)
    }

    $_cleanComponentData(): void {
        const options: any = this.$options
        Object.assign(this.$data, options?.data.apply(this))
    }

    $_isEmpty(value: any): boolean {
        return isEmpty(value)
    }

    $_openUrl(query: any): void {
        HelperUtils.openUrl(query)
    }

    $_someFieldIsDirty(fields: FieldFlagsBag): boolean {
        return HelperUtils.someFieldIsDirty(fields)
    }

    $_moveInputElementCaretToEnd(elementReference: HTMLInputElement, textLength: number): void {
        HelperUtils.moveInputElementCaretToEnd(elementReference, textLength)
    }
    $_moveQuillEditorCaretToEnd(elementReference: Quill, textLength: number): void {
        HelperUtils.moveQuillEditorCaretToEnd(elementReference, textLength)
    }

    $_resetValidator(validator: Validator): void {
        validator.pause()
        this.$nextTick(async () => {
            await validator.reset()
            validator.resume()
        })
    }

    $_keepSelectionGridWhenSearch(gridApi: GridApi, idDataNode: number, previousNodes: RowNode[], prop: string = 'id'): void {
        GridUtils.keepSelectionGridWhenSearch(gridApi, idDataNode, previousNodes, prop)
    }

    async $_getProfileImage(personnelId: number, cancelToken?: CancelToken): Promise<string | undefined | null> {
        return HelperUtils.getProfileImage(personnelId, cancelToken)
    }

    $_translateIfKeyExists(label: string): string {
        return HelperUtils.translateIfKeyExists(label)
    }

    async $_confirmNavigationBecauseOfLaboratoryChange(laboratoryId: number): Promise<boolean> {
        if (this.$store.getters.labId !== laboratoryId) {
            if (!await this.$_showConfirmationModal(this.$tc('diagnosis.wrongLaboratory'))) {
                return false
            }
            sessionStore.updateLabId(laboratoryId)
        }
        return true
    }

    async $_adviceSaveUiComponent(uiComponent: string, config: any): Promise<any> {
        return HelperUtils.adviceSaveUiComponent(uiComponent, config)
    }

    async $_showMultiEntitySelection(text: string, foundEntities: any): Promise<any> {
        return new Promise((resolve) => {
            this.$eventHub.$emit('showGlobalSearchMultipleResult', { text, entities: foundEntities })
            this.$eventHub.$on('globalSearchMultipleResultResponse', (entitySelected: any) => {
                if (entitySelected) {
                    resolve(entitySelected.entityValue)
                }
                this.$eventHub.$off('globalSearchMultipleResultResponse')
            })
        })
    }
}
